import {
  Question,
  QuestionObjective,
} from '@/resource/question/question.model';
import {
  ContentType,
  OrderType,
  SubjectiveContent,
} from '@/resource/question/question.type';
import { QuestionLanguage } from '@/resource/subject/subject.model';
import { InputData } from '@/utils/form/form-input-typing';
import replaceFormInputValue from '@/utils/form/replace-form-input-value';
import AnswerSection from '@/components/question-editor/editor-extensions/nodes/node-answer-section';
import { v4 } from 'uuid';

export const editorInitInputs: InputData[] = [
  {
    name: 'subject',
    value: {
      selectedSubject: 'science',
      selectedForm: null,
      selectedChapter: null,
      selectedChapters: [],
    },
    type: 'dropdown-subject',
    isRequired: false,
    isFormRequired: true,
    isChapterRequired: true,
    isSingleChapter: true,
  },
];

export const adminEditorInitInputs: InputData[] = [
  {
    name: 'keywords',
    value: '',
    placeholder: 'Type keywords',
    type: 'text',
  },
  {
    name: 'difficulty',
    value: '',
    placeholder: 'Difficulty',
    isRequired: true,
    type: 'number',
    minNumber: 1,
    maxNumber: 6,
  },
  {
    name: 'languages',
    value: [],
    placeholder: 'Select language',
    type: 'dropdown',
    isRequired: true,
    isMultiSelect: true,
    dropdownOptions: [
      {
        value: QuestionLanguage.english,
        text: QuestionLanguage.english,
        isSelected: false,
      },
      {
        value: QuestionLanguage.malay,
        text: QuestionLanguage.malay,
        isSelected: false,
      },
    ],
  },
  {
    name: 'mainLanguage',
    value: '',
    placeholder: 'Select main language',
    type: 'dropdown',
    isRequired: true,
    isMultiSelect: false,
    dropdownOptions: [
      {
        value: QuestionLanguage.english,
        text: QuestionLanguage.english,
        isSelected: false,
      },
      {
        value: QuestionLanguage.malay,
        text: QuestionLanguage.malay,
        isSelected: false,
      },
    ],
  },
  {
    name: 'isDraft',
    value: false,
    placeholder: 'Is draft',
    type: 'dropdown',
    isRequired: true,
    isMultiSelect: false,
    dropdownOptions: [
      {
        value: 'true',
        text: 'true',
        isSelected: false,
      },
      {
        value: 'false',
        text: 'false',
        isSelected: false,
      },
    ],
  },
];

export const updateEditorInputsByQuestion = (
  inputs: InputData[],
  question: Question,
) => {
  if (question) {
    const currentChapter = question.chapter;

    replaceFormInputValue(inputs, 'subject', {
      selectedSubject: currentChapter.subject.uuid,
      selectedForm: currentChapter.form,
      selectedChapter: currentChapter.id,
      selectedChapters: [],
    });

    replaceFormInputValue(inputs, 'keywords', question.keywords.join(', '));

    replaceFormInputValue(
      inputs,
      'isDraft',
      question.isDraft ? 'true' : 'false',
    );

    replaceFormInputValue(inputs, 'difficulty', question.difficulty);

    replaceFormInputValue(inputs, 'languages', question.languages);

    if (question.languages && question.languages.length) {
      replaceFormInputValue(inputs, 'mainLanguage', question.languages[0]);
    }
  }
};

// NOTE: Logic to randomize correct answer position for objective (was handled by backend before)
export const randomizeObjAnswerPosition = (
  question: Question,
  isSubjective: boolean,
): number => {
  const questionObjective = question as QuestionObjective;

  if (!isSubjective) {
    if (questionObjective.answerWrongJsons) {
      return Math.floor(
        Math.random() * (questionObjective.answerWrongJsons.length + 1),
      );
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

export function convertOldJSONSUbToHTML(
  json: SubjectiveContent,
  parentContentType: ContentType | null,
) {
  // NOTE: Maybe need parent type later
  parentContentType;

  let html = '';
  const isList =
    json &&
    json.type === ContentType.Parent &&
    json.config &&
    json.config.orderType &&
    json.config.orderType !== OrderType.None;

  if (json.type === ContentType.Parent) {
    if (isList) {
      if (json.config.orderType === OrderType.Alphabet) {
        html += '<ol type="a">';
      } else if (json.config.orderType === OrderType.Roman) {
        html += '<ol type="">';
      } else if (json.config.orderType === OrderType.Number) {
        html += '<ol type="1">';
      }
    } else {
      html += '<div>';
    }
  } else if (json.type === ContentType.Text) {
    html += '<p>';
    html += json.content;
    html += '</p>';
  } else if (json.type === ContentType.Image) {
    const imageObject = json.content && json.content[0];
    if (imageObject) {
      html += `<img src="${imageObject.url}" draggable="true">`;
    }
  } else if (json.type === ContentType.Table) {
    html += '<table>';
  } else if (json.type === ContentType.TableTh) {
    html += '<tr>';
  } else if (json.type === ContentType.TableTd) {
    html += '<td>';
  } else if (json.type === ContentType.Answer) {
    const answerId = `${AnswerSection.type}-${v4()}`;
    html += `<answer-section id="${answerId}" spacetype="empty">`;
    json.answer.forEach(item => {
      html += convertOldJSONSUbToHTML(item, json.type);
    });
    html += '</answer-section>';
  } else {
    // NOTE: default
    html += '<span class="default">';
    html += json.content;
    html += '</span>';
  }

  if (json.content && Array.isArray(json.content)) {
    json.content.forEach(item => {
      if (isList) {
        html += '<li>';
        html += convertOldJSONSUbToHTML(item, json.type);
        html += '</li>';
      } else if (json.type === ContentType.TableTh) {
        html += '<th>';
        html += convertOldJSONSUbToHTML(item, json.type);
        html += '</th>';
      } else {
        html += convertOldJSONSUbToHTML(item, json.type);
      }
    });
  }

  if (json.type === ContentType.Parent) {
    if (isList) {
      html += '</ol>';
    } else {
      html += '</div>';
    }
  } else if (json.type === ContentType.Table) {
    html += '</table>';
  } else if (json.type === ContentType.TableTh) {
    html += '</tr>';
  } else if (json.type === ContentType.TableTd) {
    html += '</td>';
  }

  return html;
}
